import React, { useState, useCallback, useMemo, memo } from 'react';
import {
   Box,
   Card,
   CardContent,
   Typography,
   Avatar,
   Button,
   Chip,
   Fade,
   Tooltip,
} from '@mui/material';
import {
   Download as DownloadIcon,
   AccessTime as AccessTimeIcon,
   OpenInNew as OpenInNewIcon,
   TrendingUp as TrendingUpIcon,
   Analytics as AnalyticsIcon,
   People as PeopleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

// Interface for downloader info
export interface DownloaderInfo {
   downloaderId: string;
   downloaderName: string;
   downloaderEmail?: string | null;
   downloaderPhotoURL?: string | null;
   downloadedAt?: string;
   photoId?: string;
   photoTitle?: string;
}

// Interface for the album download data (for album owners)
export interface AlbumDownload {
   id: string; // Feed card ID
   albumId: string;
   albumTitle: string;
   albumCoverUrl?: string;
   albumPhotoCount: number;
   albumIsPublic: boolean;
   albumPhotos?: string[]; // Array of photo URLs for preview
   downloaders?: DownloaderInfo[]; // Array of downloaders
   downloader?: DownloaderInfo; // Single downloader (backwards compatibility / most recent)
   totalDownloads: number;
   percentageChange: number; // Percentage change in downloads
   lastDownloadAt: Date | any;
}

interface AlbumDownloadCardProps {
   download: AlbumDownload;
   onViewAlbum?: (albumId: string) => void;
}

// Helper function to convert Firebase timestamp to Date
const toDate = (timestamp: any): Date => {
   if (!timestamp) return new Date();
   if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
   }
   if (timestamp.seconds && typeof timestamp.seconds === 'number') {
      return new Date(timestamp.seconds * 1000);
   }
   if (timestamp._seconds && typeof timestamp._seconds === 'number') {
      return new Date(timestamp._seconds * 1000);
   }
   if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      return new Date(timestamp);
   }
   if (timestamp instanceof Date) {
      return timestamp;
   }
   return new Date(timestamp);
};

// Helper function to format relative time
const formatRelativeTime = (timestamp: any): string => {
   try {
      const date = toDate(timestamp);
      if (isNaN(date.getTime())) return 'Unknown';
      return formatDistanceToNow(date, { addSuffix: true });
   } catch {
      return 'Unknown';
   }
};

const GOLD = '#C9A84C';
const TEAL = '#1D9E75';
const CARD_BG = '#111815';
const INNER_BG = '#1A2420';
const INNER_BORDER = '#253530';
const STAT_LABEL = '#546E7A';

// Extended downloader info with download count and unique photo count
interface DisplayDownloader extends DownloaderInfo {
   downloadCount?: number;
   uniquePhotoCount?: number;
}

const AlbumDownloadCard: React.FC<AlbumDownloadCardProps> = memo(({
   download,
   onViewAlbum,
}) => {
   const navigate = useNavigate();
   const [isVisible] = useState(true);

   // Memoize all downloaders processing to avoid recalculation on every render
   const {
      displayDownloaders,
      totalDownloadsCount,
      uniqueDownloadersCount,
   } = useMemo(() => {
      // Get all downloaders array - fallback to single downloader if downloaders array doesn't exist
      const allDownloaders = download.downloaders && download.downloaders.length > 0 
         ? download.downloaders 
         : download.downloader 
            ? [download.downloader] 
            : [];

      // Group downloaders by unique user and track unique photos
      const groupedDownloadersMap = new Map<string, DisplayDownloader & { uniquePhotoIds: Set<string> }>();

      allDownloaders.forEach((downloader) => {
         const key = downloader.downloaderId || downloader.downloaderEmail || 'unknown';
         
         if (groupedDownloadersMap.has(key)) {
            const existing = groupedDownloadersMap.get(key)!;
            existing.downloadCount = (existing.downloadCount || 1) + 1;
            
            if (downloader.photoId) {
               existing.uniquePhotoIds.add(downloader.photoId);
            }
            
            if (downloader.downloadedAt && (!existing.downloadedAt || downloader.downloadedAt > existing.downloadedAt)) {
               existing.downloadedAt = downloader.downloadedAt;
            }
         } else {
            const uniquePhotoIds = new Set<string>();
            if (downloader.photoId) {
               uniquePhotoIds.add(downloader.photoId);
            }
            
            groupedDownloadersMap.set(key, {
               ...downloader,
               downloadCount: 1,
               uniquePhotoIds,
            });
         }
      });

      // Convert map to array and sort by most recent
      const uniqueDownloaders = Array.from(groupedDownloadersMap.values()).map(downloader => ({
         ...downloader,
         downloadCount: downloader.downloadCount || 1,
         uniquePhotoCount: downloader.uniquePhotoIds?.size || 0,
      })).sort((a, b) => {
         const aTime = a.downloadedAt ? new Date(a.downloadedAt).getTime() : 0;
         const bTime = b.downloadedAt ? new Date(b.downloadedAt).getTime() : 0;
         return bTime - aTime;
      });

      return {
         displayDownloaders: uniqueDownloaders,
         totalDownloadsCount: allDownloaders.length,
         uniqueDownloadersCount: uniqueDownloaders.length,
      };
   }, [download.downloaders, download.downloader]);

   // Memoize other computed values
   const percentageChangeText = useMemo(() => {
      if (download.percentageChange > 0) return `+${download.percentageChange.toFixed(0)}%`;
      if (download.percentageChange < 0) return `${download.percentageChange.toFixed(0)}%`;
      return '0%';
   }, [download.percentageChange]);
   
   const isPositiveChange = download.percentageChange > 0;

   // Handle view album
   const handleViewAlbum = useCallback(() => {
      if (onViewAlbum) {
         onViewAlbum(download.albumId);
      } else {
         navigate(`/albums/${download.albumId}`);
      }
   }, [download.albumId, navigate, onViewAlbum]);

   return (
      <Fade in={isVisible} timeout={300}>
         <Card
            sx={{
               borderRadius: '12px',
               boxShadow: 'none',
               border: `1px solid ${TEAL}`,
               borderTop: `4px solid ${TEAL}`,
               overflow: 'visible',
               background: CARD_BG,
               transition: 'all 0.2s ease-in-out',
               '&:hover': {
                  boxShadow: `0 4px 16px rgba(201,168,76,0.12)`,
                  transform: 'translateY(-1px)',
               },
            }}
         >
            <CardContent sx={{ p: 2.5 }}>
               {/* Header with Badge */}
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'space-between',
                     alignItems: 'flex-start',
                     mb: 2,
                  }}
               >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                     <AnalyticsIcon sx={{ color: GOLD, fontSize: 20 }} />
                     <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                        {download.albumTitle}
                     </Typography>
                  </Box>

                  <Chip
                     icon={<DownloadIcon sx={{ fontSize: 14 }} />}
                     label="Downloads"
                     size="small"
                     sx={{
                        height: 24,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: 'rgba(201,168,76,0.12)',
                        color: GOLD,
                        border: '1px solid rgba(201,168,76,0.25)',
                        '& .MuiChip-icon': {
                           color: GOLD,
                        },
                     }}
                  />
               </Box>

               {/* Downloaders Section */}
               <Box
                  sx={{
                     p: 1.5,
                     mb: 2,
                     backgroundColor: INNER_BG,
                     borderRadius: 1.5,
                     border: `1px solid ${INNER_BORDER}`,
                     overflow: 'visible',
                  }}
               >
                  {/* Downloaders Header with Stats */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PeopleIcon sx={{ fontSize: 18, color: TEAL }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                           {uniqueDownloadersCount} Downloader{uniqueDownloadersCount !== 1 ? 's' : ''}
                           <Typography component="span" variant="caption" sx={{ color: '#B0BEC5', ml: 0.5 }}>
                              ({totalDownloadsCount} total download{totalDownloadsCount !== 1 ? 's' : ''})
                           </Typography>
                        </Typography>
                     </Box>
                  </Box>

                  {/* Horizontal Scrollable Downloaders List */}
                  <Box
                     sx={{
                        width: '100%',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        pb: 1,
                        '&::-webkit-scrollbar': {
                           height: 6,
                        },
                        '&::-webkit-scrollbar-track': {
                           backgroundColor: INNER_BG,
                           borderRadius: 3,
                        },
                        '&::-webkit-scrollbar-thumb': {
                           backgroundColor: INNER_BORDER,
                           borderRadius: 3,
                           '&:hover': {
                              backgroundColor: TEAL,
                           },
                        },
                     }}
                  >
                     <Box 
                        sx={{ 
                           display: 'inline-flex',
                           flexDirection: 'row',
                           flexWrap: 'nowrap',
                           gap: 1.5, 
                           minWidth: 'min-content',
                           width: 'max-content',
                        }}
                     >
                        {displayDownloaders.map((downloader, index) => {
                           const downloadCount = downloader.downloadCount || 1;
                           const uniquePhotoCount = downloader.uniquePhotoCount || 0;
                           
                           return (
                              <Box
                                 key={index}
                                 sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    width: 110,
                                    minWidth: 110,
                                    p: 1.5,
                                    borderRadius: 2,
                                    backgroundColor: INNER_BG,
                                    border: `1px solid ${INNER_BORDER}`,
                                    position: 'relative',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                       backgroundColor: '#1F2E28',
                                       transform: 'translateY(-2px)',
                                       boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                    },
                                 }}
                              >
                                 {/* Photo count badge showing unique photos downloaded */}
                                 {uniquePhotoCount > 0 && (
                                    <Box
                                       sx={{
                                          position: 'absolute',
                                          top: -8,
                                          right: -8,
                                          minWidth: 24,
                                          height: 24,
                                          borderRadius: '12px',
                                          backgroundColor: GOLD,
                                          color: '#000',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '0.7rem',
                                          fontWeight: 700,
                                          border: `2px solid ${CARD_BG}`,
                                          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                          px: 0.5,
                                       }}
                                    >
                                       {uniquePhotoCount}
                                    </Box>
                                 )}

                                 {/* Avatar with download indicator */}
                                 <Box sx={{ position: 'relative', mb: 1 }}>
                                    <Avatar
                                       src={downloader.downloaderPhotoURL || undefined}
                                       sx={{
                                          width: 44,
                                          height: 44,
                                          fontSize: '1rem',
                                          fontWeight: 600,
                                          border: `3px solid ${TEAL}`,
                                          backgroundColor: 'rgba(29,158,117,0.2)',
                                          color: TEAL,
                                       }}
                                    >
                                       {downloader.downloaderName?.[0]?.toUpperCase() || 'U'}
                                    </Avatar>
                                    {/* Download indicator dot */}
                                    <Box
                                       sx={{
                                          position: 'absolute',
                                          bottom: 0,
                                          right: 0,
                                          width: 14,
                                          height: 14,
                                          borderRadius: '50%',
                                          backgroundColor: TEAL,
                                          border: `2px solid ${INNER_BG}`,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                       }}
                                    >
                                       <DownloadIcon sx={{ fontSize: 8, color: '#fff' }} />
                                    </Box>
                                 </Box>

                                 {/* Name */}
                                 <Typography
                                    variant="body2"
                                    sx={{
                                       fontWeight: 600,
                                       color: '#FFFFFF',
                                       textAlign: 'center',
                                       overflow: 'hidden',
                                       textOverflow: 'ellipsis',
                                       whiteSpace: 'nowrap',
                                       width: '100%',
                                       lineHeight: 1.2,
                                       fontSize: '0.8rem',
                                    }}
                                 >
                                    {downloader.downloaderName || 'Unknown'}
                                 </Typography>

                                 {/* Photo count subtitle */}
                                 <Tooltip title={downloader.downloaderEmail || ''} arrow>
                                    <Typography
                                       variant="caption"
                                       sx={{
                                          color: '#B0BEC5',
                                          textAlign: 'center',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          width: '100%',
                                          fontSize: '0.65rem',
                                          lineHeight: 1.3,
                                       }}
                                    >
                                       {uniquePhotoCount > 0 
                                          ? `${uniquePhotoCount} ${uniquePhotoCount === 1 ? 'photo' : 'photos'}`
                                          : (downloader.downloaderEmail || 'Downloader')}
                                    </Typography>
                                 </Tooltip>

                                 {/* Download Time */}
                                 {downloader.downloadedAt && (
                                    <Typography
                                       variant="caption"
                                       sx={{
                                          color: STAT_LABEL,
                                          fontSize: '0.55rem',
                                          mt: 0.5,
                                          textAlign: 'center',
                                       }}
                                    >
                                       {formatRelativeTime(downloader.downloadedAt)}
                                    </Typography>
                                 )}
                              </Box>
                           );
                        })}
                     </Box>
                  </Box>
               </Box>

               {/* Stats Section */}
               <Box
                  sx={{
                     display: 'flex',
                     gap: 1.5,
                     mb: 2,
                     p: 1.5,
                     backgroundColor: INNER_BG,
                     borderRadius: 1.5,
                     border: `1px solid ${INNER_BORDER}`,
                  }}
               >
                  {/* Total Downloads */}
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <DownloadIcon sx={{ fontSize: 16, color: TEAL }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, lineHeight: 1 }}>
                           {download.totalDownloads}
                        </Typography>
                     </Box>
                     <Typography variant="caption" sx={{ color: STAT_LABEL, fontSize: '0.65rem' }}>
                        Total Downloads
                     </Typography>
                  </Box>

                  {/* Percentage Change */}
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <TrendingUpIcon 
                           sx={{ 
                              fontSize: 16, 
                              color: isPositiveChange ? TEAL : download.percentageChange < 0 ? '#ef4444' : STAT_LABEL,
                           }} 
                        />
                        <Typography 
                           variant="h6" 
                           sx={{ 
                              fontWeight: 700, 
                              color: isPositiveChange ? TEAL : download.percentageChange < 0 ? '#ef4444' : STAT_LABEL,
                              lineHeight: 1,
                           }}
                        >
                           {percentageChangeText}
                        </Typography>
                     </Box>
                     <Typography variant="caption" sx={{ color: STAT_LABEL, fontSize: '0.65rem' }}>
                        Change
                     </Typography>
                  </Box>

                  {/* Unique Downloaders */}
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <PeopleIcon sx={{ fontSize: 16, color: TEAL }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, lineHeight: 1 }}>
                           {uniqueDownloadersCount}
                        </Typography>
                     </Box>
                     <Typography variant="caption" sx={{ color: STAT_LABEL, fontSize: '0.65rem' }}>
                        Users
                     </Typography>
                  </Box>
               </Box>

               {/* Timestamp */}
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                  <AccessTimeIcon sx={{ fontSize: 14, color: STAT_LABEL }} />
                  <Typography variant="caption" sx={{ color: STAT_LABEL }}>
                     Last download {formatRelativeTime(download.lastDownloadAt)}
                  </Typography>
               </Box>

               {/* Action Button */}
               <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                  onClick={handleViewAlbum}
                  sx={{
                     backgroundColor: TEAL,
                     color: '#ffffff',
                     fontWeight: 600,
                     textTransform: 'none',
                     borderRadius: 1.5,
                     py: 1,
                     '&:hover': { backgroundColor: '#178a65' },
                  }}
               >
                  View Album
               </Button>
            </CardContent>
         </Card>
      </Fade>
   );
});

// Display name for debugging
AlbumDownloadCard.displayName = 'AlbumDownloadCard';

export default AlbumDownloadCard;
